import { useMediaQuery } from '@mui/system'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { isValid } from 'date-fns'
import { useContext, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { LocaleContext } from 'components/common/LocaleContext'
import {
  InputErrorLabel,
  InputLabel,
  InputLabelBlock
} from 'components/common/TextInput'
import { themeUtils } from 'styles/theme'
import {
  BirthDateInputErrorSXProps,
  BirthDateInputSXProps
} from './BirthDateInput.style'

interface BirthDateInputProps {
  fullSize?: boolean
  inputLabel?: string
  fieldName: string
  errorMessage?: string
  disabled?: boolean
  noMargin?: boolean
}

export const BirthDateInput = ({
  fullSize,
  inputLabel,
  fieldName,
  errorMessage,
  disabled,
  noMargin
}: BirthDateInputProps) => {
  const { control } = useFormContext()
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const [open, setOpen] = useState(false)
  const { fnsLocale, selectedMuiDateLocale } = useContext(LocaleContext)

  return (
    <InputLabelBlock fullSize={fullSize} noMargin={noMargin}>
      {inputLabel && <InputLabel>{inputLabel}</InputLabel>}
      <div>
        <LocalizationProvider
          localeText={
            selectedMuiDateLocale.components.MuiLocalizationProvider
              .defaultProps.localeText
          }
          dateAdapter={AdapterDateFns}
          adapterLocale={fnsLocale}
        >
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
              <DatePicker
                sx={{
                  ...BirthDateInputSXProps,
                  ...(errorMessage && BirthDateInputErrorSXProps)
                }}
                onChange={(val) => {
                  if (isValid(val) && val instanceof Date) {
                    const value = new Date(
                      Date.UTC(val.getFullYear(), val.getMonth(), val.getDate())
                    )
                    field.onChange(value)
                  } else {
                    field.onChange(undefined)
                  }
                }}
                value={
                  field.value instanceof Date
                    ? new Date(
                        field.value.getUTCFullYear(),
                        field.value.getUTCMonth(),
                        field.value.getUTCDate()
                      )
                    : null
                }
                disabled={disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                openTo="year"
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    onClick: () => setOpen(isMobile),
                    // Pass the ref from RHF so it can focus the input on error
                    inputRef: field.ref,
                    error: !!errorMessage
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      </div>
      {errorMessage && <InputErrorLabel>{errorMessage}</InputErrorLabel>}
    </InputLabelBlock>
  )
}
